import { useState } from "react";
import axios from "axios";
import { Mail, Linkedin, Instagram, Send, MapPin } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Reveal } from "@/components/Reveal";
import { images } from "@/data/images";
import { site } from "@/data/site";

const initial = { name: "", email: "", phone: "", subject: "", message: "", company: "" };
const API = process.env.REACT_APP_BACKEND_URL;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);

  const update = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return; // guard against duplicate submissions

    // Validate required fields before submitting.
    const name = form.name.trim();
    const email = form.email.trim();
    const subject = form.subject.trim();
    const message = form.message.trim();
    if (!name || !email || !subject || !message) {
      toast.error("Please fill in all required fields", {
        description: "Name, email, subject, and message are required.",
      });
      return;
    }
    if (!EMAIL_RE.test(email)) {
      toast.error("Please enter a valid email address");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/api/contact`, {
        name,
        email,
        phone: form.phone.trim(),
        subject,
        message,
        company: form.company, // honeypot (should be empty)
      });
      setForm(initial);
      toast.success("Message sent!", {
        description:
          "Thank you for reaching out! Your message has been sent successfully. I'll get back to you as soon as possible.",
      });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error("Message not sent", {
        description:
          detail || "Something went wrong. Please try again or email me directly.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const channels = [
    { icon: Mail, label: "Email", value: site.contact.email, href: `mailto:${site.contact.email}`, testid: "contact-email" },
    { icon: Linkedin, label: "LinkedIn", value: site.contact.linkedinLabel, href: site.contact.linkedin, testid: "contact-linkedin" },
    { icon: Instagram, label: "Instagram", value: site.contact.instagramLabel, href: site.contact.instagram, testid: "contact-instagram" },
    { icon: MapPin, label: "Based In", value: site.location, href: null, testid: "contact-location" },
  ];

  return (
    <div data-testid="contact-page">
      <PageHeader
        eyebrow="Contact"
        title="Let's build something."
        intro="Whether you're a parent, an athletic director, a program, a player, or a fellow coach, I'd love to talk about developing athletes, building culture, and growing programs."
        image={images.arenaDark}
      />

      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            {/* Channels */}
            <div className="lg:col-span-5">
              <Reveal>
                <p className="label-eyebrow mb-6">Direct Channels</p>
                <div className="space-y-px bg-white/10 border border-white/10">
                  {channels.map((c) => {
                    const Inner = (
                      <>
                        <span className="h-11 w-11 grid place-items-center bg-burnt/15 text-burnt shrink-0">
                          <c.icon size={20} />
                        </span>
                        <div>
                          <p className="text-xs uppercase tracking-[0.15em] text-zinc-500">
                            {c.label}
                          </p>
                          <p className="text-white mt-0.5 break-all">{c.value}</p>
                        </div>
                      </>
                    );
                    return c.href ? (
                      <a
                        key={c.label}
                        href={c.href}
                        target={c.href.startsWith("http") ? "_blank" : undefined}
                        rel="noreferrer"
                        data-testid={c.testid}
                        className="flex items-center gap-4 bg-ink-surface p-5 hover:bg-ink-elevated transition-colors"
                      >
                        {Inner}
                      </a>
                    ) : (
                      <div
                        key={c.label}
                        data-testid={c.testid}
                        className="flex items-center gap-4 bg-ink-surface p-5"
                      >
                        {Inner}
                      </div>
                    );
                  })}
                </div>
              </Reveal>
            </div>

            {/* Form */}
            <div className="lg:col-span-7">
              <Reveal>
                <form
                  data-testid="contact-form"
                  onSubmit={handleSubmit}
                  className="border border-white/10 bg-ink-surface p-7 md:p-10"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <Field label="Name" name="name" value={form.name} onChange={update} placeholder="Your name" testid="contact-input-name" />
                    <Field label="Email" name="email" type="email" value={form.email} onChange={update} placeholder="you@email.com" testid="contact-input-email" />
                  </div>
                  <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-5">
                    <Field label="Phone (optional)" name="phone" type="tel" value={form.phone} onChange={update} placeholder="Your phone number" testid="contact-input-phone" required={false} />
                    <Field label="Subject" name="subject" value={form.subject} onChange={update} placeholder="What's this about?" testid="contact-input-subject" />
                  </div>
                  {/* Honeypot — hidden from users, catches bots. */}
                  <div aria-hidden="true" className="absolute left-[-9999px] top-[-9999px] h-0 w-0 overflow-hidden">
                    <label>
                      Company
                      <input
                        type="text"
                        name="company"
                        tabIndex={-1}
                        autoComplete="off"
                        value={form.company}
                        onChange={update}
                        data-testid="contact-input-company"
                      />
                    </label>
                  </div>
                  <div className="mt-5">
                    <label htmlFor="message" className="label-eyebrow block mb-2">Message</label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      value={form.message}
                      onChange={update}
                      rows={6}
                      data-testid="contact-input-message"
                      placeholder="Tell me a bit about your program or opportunity..."
                      className="w-full bg-ink-base border border-white/10 text-white px-4 py-3 focus:outline-none focus:border-burnt placeholder:text-zinc-600 resize-none"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="contact-submit-button"
                    className="group mt-7 inline-flex items-center gap-2 bg-burnt hover:bg-burnt-hover disabled:opacity-60 text-white font-medium px-7 py-3.5 transition-colors"
                  >
                    {submitting ? "Sending..." : "Send Message"}
                    <Send size={16} className="transition-transform group-hover:translate-x-1" />
                  </button>
                </form>
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

const Field = ({ label, name, type = "text", value, onChange, placeholder, testid, required = true }) => (
  <div>
    <label htmlFor={name} className="label-eyebrow block mb-2">{label}</label>
    <input
      id={name}
      type={type}
      name={name}
      required={required}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      data-testid={testid}
      className="w-full bg-ink-base border border-white/10 text-white px-4 py-3 focus:outline-none focus:border-burnt placeholder:text-zinc-600"
    />
  </div>
);
